import {gsap} from 'gsap';
import {ScrollToPlugin} from 'gsap/ScrollToPlugin';
import {
	HEADER_STICKY_SCROLL_RATIO,
	HEADER_STICKY_HEIGHT,
	STICKY_NAV_HEIGHT,
	HEADER_DEFAULT_HEIGHT,
} from '#shared/constants';

gsap.registerPlugin(ScrollToPlugin);

let globalScrollLock = false;

export interface ScrollToSectionOptions {
	duration?: number
	addHeaderOffset?: boolean
	scrollOffset?: number
}

export type ScrollTarget = string | Element | null | undefined;

export function normalizeHashSelector(selector: string): string {
	let decodedSelector = selector;
	if (selector.startsWith('#')) {
		try {
			decodedSelector = decodeURIComponent(selector);
		} catch {
			decodedSelector = selector;
		}
	}

	if (!decodedSelector.startsWith('#')) {
		return decodedSelector;
	}

	const hashWithoutQuery = decodedSelector.split('?')[0];
	return hashWithoutQuery || '#';
}

function findElement(selector: string): Element | null {
	const normalizedSelector = normalizeHashSelector(selector);

	if (normalizedSelector.startsWith('#')) {
		const elementId = normalizedSelector.slice(1);
		if (!elementId) {
			return null;
		}

		return document.getElementById(elementId);
	}

	try {
		return document.querySelector(normalizedSelector);
	} catch (error) {
		if (import.meta.dev) {
			console.warn(`Invalid selector ${selector}`, error);
		}
		return null;
	}
}

function waitForElement(selector: string, timeout = 2000): Promise<Element | null> {
	const startTime = Date.now();
	const normalizedSelector = normalizeHashSelector(selector);

	return new Promise((resolve) => {
		const checkElement = () => {
			const element = findElement(normalizedSelector);

			if (element) {
				resolve(element);
			} else if (Date.now() - startTime > timeout) {
				if (import.meta.dev) {
					console.warn(`Element ${normalizedSelector} not found within ${timeout}ms`);
				}
				resolve(null);
			} else {
				setTimeout(checkElement, 50);
			}
		};

		checkElement();
	});
}

export default async function scrollToSection(
	section: ScrollTarget,
	{
		duration = 0.3,
		addHeaderOffset = true,
		scrollOffset = 0,
	}: ScrollToSectionOptions = {},
): Promise<void> {
	if (typeof window === 'undefined') {
		return;
	}

	let targetElement: Element | null = null;

	if (typeof section === 'string') {
		await nextTick();
		targetElement = await waitForElement(section);

		if (!targetElement) {
			return;
		}
	} else {
		targetElement = section ?? null;
		await nextTick();
	}

	if (!targetElement) {
		return;
	}

	const performScroll = async (): Promise<void> => {
		if (globalScrollLock) {
			return;
		}
		globalScrollLock = true;

		try {
			let finalOffset = scrollOffset;

			if (addHeaderOffset) {
				const currentScrollY = window.scrollY;
				const targetPosition = (targetElement as HTMLElement).offsetTop;
				const minScrollThreshold = HEADER_DEFAULT_HEIGHT * HEADER_STICKY_SCROLL_RATIO;

				const isScrollingDown = targetPosition > currentScrollY;
				const scrollDistance = Math.abs(targetPosition - currentScrollY);

				const headerElement = document.querySelector('#header') as HTMLElement;
				const currentHeaderHeight = headerElement?.offsetHeight || HEADER_DEFAULT_HEIGHT;
				const currentScrollThreshold = currentHeaderHeight * HEADER_STICKY_SCROLL_RATIO;

				let headerHeight: number;

				if (isScrollingDown) {
					const isCurrentlyExpanded = currentHeaderHeight === HEADER_DEFAULT_HEIGHT;
					const willCollapseHeader = targetPosition > minScrollThreshold && scrollDistance >= currentScrollThreshold && isCurrentlyExpanded;
					const isCurrentlyCollapsed = currentHeaderHeight === HEADER_STICKY_HEIGHT;
					const willStayCollapsed = targetPosition > minScrollThreshold && isCurrentlyCollapsed;

					if (willCollapseHeader || willStayCollapsed) {
						headerHeight = HEADER_STICKY_HEIGHT;
					} else {
						headerHeight = currentHeaderHeight;
					}
				} else if (scrollDistance < currentScrollThreshold) {
					headerHeight = currentHeaderHeight;
				} else {
					headerHeight = HEADER_DEFAULT_HEIGHT;
				}

				finalOffset += headerHeight;

				const stickyNav = document.querySelector('#sticky-anchor-nav') as HTMLElement;
				if (stickyNav) {
					finalOffset += STICKY_NAV_HEIGHT;
				}
			}

			await gsap.to(window, {
				duration,
				scrollTo: {
					y: targetElement,
					offsetY: finalOffset,
					autoKill: false,
				},
				ease: 'none',
			});
		} finally {
			globalScrollLock = false;
		}
	};

	await performScroll();
}
